/**
 * The dialog.
 *
 * Built on `@radix-ui/react-dialog`, which was already a dependency — DESIGN.md §10: we take a
 * headless library for behaviour and accessibility, and we draw the surface ourselves. Radix
 * gives us the four things a hand-rolled dialog always gets wrong: a real focus trap, focus
 * restored to the trigger on close, Escape, and a backdrop that dismisses.
 *
 * Motion (DESIGN.md §6): 180ms — `--duration-base` — entering from where the thing belongs. A
 * centred dialog rises the last few pixels into the middle of the screen; a side panel comes in
 * from the edge it is anchored to. `prefers-reduced-motion` removes the travel, not the dialog:
 * the keyframes still run, at 0.01ms, so Radix still unmounts on close.
 *
 * WHAT THIS COMPONENT DELIBERATELY DOES NOT HAVE (DESIGN.md §11, rules 3 and 4):
 *
 *   - No `confirm()` convenience, no `<ConfirmDialog>`, no `destructive` preset. Anything that
 *     can be undone must be done immediately and offered back through a Toast with an undo
 *     action. A confirmation dialog for an undoable action is a tax on every correct use of the
 *     feature to protect against the rare wrong one.
 *   - No default open-on-navigate helper. If it could have been a section on the page, it
 *     should have been a section on the page.
 */
import * as Radix from '@radix-ui/react-dialog';
import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { RowViewer } from '@tvitvi/shared';

import { cn } from '@/lib/cn';

import { Button } from './Button';
import { runEscapeStep } from './escape-steps';
import { ICONS } from './icons';
import { registerOpenModal } from './modal-nesting';
import { stopPortalClick } from './portal-events';
import { RowViewerProvider } from './row-viewer';
import { InsideSurface } from './surface';
import { usePortalTheme } from './use-portal-theme';

export type ModalPlacement = 'center' | 'right';
export type ModalSize = 'sm' | 'md' | 'lg' | 'wide' | 'xl';

const SIZE: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  /*
   * 720 px — DEN BREDE SKUFFEN (QA-runde 12, K-7 / J1).
   *
   * Atle har godkjent at artistpopupen i Booking blir en skuff framfor en modal, og at den skuffen
   * er bred. Tallet er hans; det som er målt er hvorfor ingen av de eksisterende passet. Kortet har
   * et bilde til venstre og en faneliste med fem faner over et innhold i to kolonner: `lg` (672) gir
   * 320 px per kolonne når bildet har fått sitt, som er smalere enn en e-postadresse, og `xl` (760)
   * er `EntityPanel`s egen bredde og bærer en helt annen begrunnelse (tidslinja på skjerm 7). To
   * flater med hver sin grunn til å være brede skal ikke dele ett tall — da flytter den ene den
   * andre neste gang noen justerer.
   */
  wide: 'max-w-[720px]',
  /*
   * 760 px — bredden UX-ordren gir `EntityPanel` (A2 og A4).
   *
   * Den er ikke valgt her, den er målt i mockupen: skjerm 7 har en timelinje med tolv timetall og
   * en hendelsesrad med tid, tittel, en underlinje på fire ledd og et statusmerke til høyre. Under
   * 700 px bryter underlinja, og en tidslinje der hver rad er to linjer er en liste igjen.
   */
  xl: 'max-w-[760px]',
};

/**
 * ÉN HANDLING I BUNNLINJA (grep I1, QA13 punkt 34 og 94).
 *
 * 35 av 106 dialoger hadde ingen blå knapp, fordi `footer` var fri JSX og `Button` er grå uten
 * `variant`. Med `primaryAction` tegner dialogen primærknappen selv — blå, ytterst til høyre,
 * én — og kallstedet kan ikke glemme det.
 */
export interface ModalAction {
  label: string;
  onClick?: () => void;
  loading?: boolean;
  /** Se `Button.disabledReason`: knappen står, grå, med grunnen i en tooltip. */
  disabledReason?: string | null;
  /** Id-en til et `<form>` knappen sender (`type="submit"`). Uten: en vanlig knapp. */
  form?: string;
  /** Rød tekst på en sekundærhandling («Slett»). Aldri på primærhandlingen. */
  danger?: boolean;
  'data-testid'?: string;
}

/** «Avbryt»: `true` er standardteksten som lukker, et objekt gir egen tekst eller handling. */
export type ModalCancel =
  boolean | { label?: string; onClick?: () => void; 'data-testid'?: string };

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Required. A dialog without a title is a dialog a screen reader announces as nothing. */
  title: string;
  /**
   * Skjuler tittelen VISUELT, aldri fra tilgjengelighetstreet.
   *
   * For en dialog som har sin egen rike overskrift inni seg — artistkortet i booking har et bilde,
   * navnet i stor skrift, piller og en statusprikk — og der tittellinja over blir det samme navnet
   * en gang til, to linjer fra hverandre. `Radix.Title` blir stående som `sr-only`, så dialogen
   * annonseres nøyaktig som før; det er bare gjentakelsen på skjermen som forsvinner.
   *
   * Ikke en snarvei til «dialog uten tittel»: `title` er fortsatt påkrevd, og en dialog som skjuler
   * den uten å tegne navnet sitt selv er en dialog som ser ut som ingenting.
   */
  titleHidden?: boolean;
  /** Wired to `aria-describedby`. Omit it rather than passing an empty string. */
  description?: string;
  children: ReactNode;
  /**
   * IKONKNAPPER VED SIDEN AV LUKKEKNAPPEN, øverst til høyre.
   *
   * ARTISTS.md §3.1 ber om «two icon buttons at the right of the header: overflow (⋮) and close»,
   * og §3.2 begrunner hvorfor: Hicks lov. «Bytt format», «kopier lenke» og «arkiver» brukes sjelden
   * og skal ikke ligge synlig i en dialog man åpner for å svare på en pris.
   *
   * BARE IKONER HER. Slotten står i en smal rad ved siden av krysset; en knapp med tekst ville
   * dyttet tittelen ut av linja på en smal skjerm. Handlinger med ord hører til i footeren.
   */
  headerActions?: ReactNode;
  /**
   * OM KRYSSET ØVERST TIL HØYRE SKAL TEGNES I DET HELE TATT. Standard: ja.
   *
   * Sett den til `false` for en dialog kalleren med vilje ikke lar lukke — da svarer
   * `onOpenChange` uansett ikke på et lukkeforsøk, og krysset er en knapp som ikke gjør noe.
   * QA14 runde 2, Q1: «Lukk lukker ikke.» Det stemte, og det var verre enn en manglende knapp —
   * en kontroll som ser trykkbar ut og ikke virker lærer mennesket at grensesnittet lyver.
   *
   * Radix' egen `Escape` og klikk utenfor går fortsatt gjennom `onOpenChange`, så en dialog som
   * skal kunne lukkes MÅ svare der framfor å skjule krysset.
   */
  dismissible?: boolean;
  /**
   * The action row as free JSX. ELDRE FORM — nye dialoger bruker `primaryAction`,
   * `secondaryActions` og `cancel`, og `modal-footer.spec.ts` holder telling på dem som ennå
   * tegner en lagre- eller sendeknapp her uten å gjøre den blå.
   */
  footer?: ReactNode;
  /** Den ene blå knappen, ytterst til høyre. Se `ModalAction`. */
  primaryAction?: ModalAction;
  /**
   * Tonale knapper foran «Avbryt». En farlig handling (`danger`) står ytterst til venstre. De
   * regnes som endringer: i lesemodus tegnes de ikke.
   */
  secondaryActions?: readonly ModalAction[];
  cancel?: ModalCancel;
  /**
   * HVA LESEREN KAN GJØRE MED RADEN DIALOGEN VISER (D1, `row-viewer.tsx`).
   *
   * Gir kroppen en `RowViewerProvider`, så hvert `Field` inni blir skrivebeskyttet eller låst med
   * grunn. Uten `canEdit` er dialogen en visning: bunnlinja blir én «Lukk», og verken
   * primærhandlingen, sekundærhandlingene eller en fri `footer` tegnes.
   */
  viewer?: RowViewer | null;
  /** Testid-en til «Lukk» i lesemodus, når dialogen har en egen fra før. Standard `modal-close`. */
  closeTestId?: string;
  placement?: ModalPlacement;
  size?: ModalSize;
  className?: string;
  'data-testid'?: string;
}

export function Modal({
  open,
  onOpenChange,
  title,
  titleHidden = false,
  description,
  children,
  headerActions,
  dismissible = true,
  footer,
  primaryAction,
  secondaryActions,
  cancel,
  viewer,
  closeTestId = 'modal-close',
  placement = 'center',
  size = 'md',
  className,
  'data-testid': testId,
}: ModalProps): ReactNode {
  const { t } = useTranslation();
  const CloseIcon = ICONS.close;

  const isSheet = placement === 'right';

  /*
   * Focus restoration, owned here rather than left to Radix.
   *
   * Radix restores focus to `Dialog.Trigger`, which only exists if the dialog was opened from
   * one. Half of the dialogs in this product are opened from a row menu, a keyboard shortcut or
   * a mutation result, and in those cases Radix has nothing to restore to and focus falls to
   * `<body>` — which drops a keyboard user back at the top of the page.
   *
   * `onOpenAutoFocus` fires while the previously focused element is still focused, so this is
   * where we can see it; `onCloseAutoFocus` fires on the way out, and preventing its default
   * takes over the restore for both trigger and non-trigger dialogs alike.
   */
  const restoreTo = useRef<Element | null>(null);

  /*
   * Colour mode, carried across the portal (DESIGN.md §4).
   *
   * Both the backdrop and the panel are appended to `document.body`, outside every
   * `[data-theme]` subtree, so both resolve their tokens against the DOCUMENT's mode unless they
   * are told otherwise. On `/design` — which renders the gallery once in each mode side by side
   * — that is a light dialog over a dark page.
   *
   * THE ANCHOR IS THE DECLARATION SITE, not the trigger. Every other portal in this directory
   * reads the mode off its own trigger, but a dialog has no trigger it can count on: `ModalTrigger`
   * is optional, and half of these are opened from a row menu, a keyboard shortcut or the result
   * of a mutation. What a dialog does always have is a place in the React tree, and that place is
   * inside the pane it belongs to. The marker below is that place, made readable to `closest()`.
   *
   * Captured in a LAYOUT effect keyed on `open`, not in `onOpenChange`: this component is
   * controlled, so Radix only reports the closes it causes itself and never the opens the caller
   * performs by setting the prop. Layout, so the stamp lands before the browser paints — an
   * effect that ran after paint would show one frame of the wrong mode, which is the same flash
   * this rule exists to remove.
   */
  const { anchorRef, capture, attributes } = usePortalTheme();
  useLayoutEffect(() => {
    if (open) capture();
  }, [open, capture]);

  /*
   * «Modal over modal — aldri» (K-7). Se `modal-nesting.ts` for hvorfor det er et register og
   * ikke en kontekst, og hvorfor det advarer framfor å kaste.
   */
  useEffect(() => {
    if (!open) return undefined;
    return registerOpenModal(title);
  }, [open, title]);

  return (
    <Radix.Root open={open} onOpenChange={onOpenChange}>
      {/* Not rendered, not focusable, not announced — a position in the tree, nothing more. */}
      <span ref={anchorRef} hidden />
      <Radix.Portal>
        <Radix.Overlay
          {...attributes}
          onClick={stopPortalClick}
          className={cn(
            'fixed inset-0 z-40 bg-[color:var(--color-overlay)]',
            'data-[state=open]:animate-[tvitvi-fade-in_var(--duration-base)_var(--ease-out)]',
            'data-[state=closed]:animate-[tvitvi-fade-out_var(--duration-fast)_var(--ease-in)]',
          )}
        />
        {/*
          The centring wrapper is inert to the pointer, so a click that lands beside the panel
          reaches the overlay and dismisses — without the panel itself having to be positioned
          with a transform that would then fight the entry animation.
        */}
        {/*
          `onClick` below is NOT AN INTERACTION, IT IS AN EVENT BOUNDARY. jsx-a11y reads a click
          handler on a plain <div> as an unlabelled control and asks for a keyboard equivalent.
          There is nothing here to activate: the handler only stops a click that happened INSIDE
          this portal from continuing up the React tree into whatever opened the dialog. A
          keyboard user never produces the event, and a key handler would invent a control that
          does not exist. The rule is right in general and wrong about this one element.
        */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div
          // The panel's own root, and the second of the two things this portal appends to the
          // body. The overlay is its SIBLING, not its child, so one stamp cannot cover both —
          // and neither can one click guard. See `stopPortalClick`.
          {...attributes}
          onClick={stopPortalClick}
          className={cn(
            'pointer-events-none fixed inset-0 z-50 flex',
            /*
             * GJESTELINJA STÅR OVER DIALOGLAGET (P10, funn D): den er navigasjon (Glass 2, §16.3),
             * et bånd øverst over hele skallet. Skuffen og dialogen begynner under den, så «Tilbake»
             * aldri ligger under et panel. Overlegget (z-40) står der det står; linja er z-60.
             */
            '[body:has([data-guest-bar])_&]:top-[calc(var(--topbar-height)+var(--shell-gap)*2)]',
            isSheet ? 'items-stretch justify-end' : 'items-center justify-center p-4',
          )}
        >
          <Radix.Content
            data-testid={testId}
            /*
             * TOASTEN ER IKKE «UTENFOR» (QA5-D). «Angre» i toasten ligger utenfor panelet, og Radix
             * regnet klikket som et klikk ved siden av — så angringen lukket panelet den angret i.
             */
            onInteractOutside={(event) => {
              const target = event.target;
              /* Heller ikke gjestelinja (P10, funn D): «Tilbake» går ut, panelet følger med. */
              if (
                target instanceof Element &&
                target.closest('[data-toast-region], [data-guest-bar]') !== null
              ) {
                event.preventDefault();
              }
            }}
            /* 239.50: et steg inne i vinduet (`escape-steps.ts`) tar Escape og går ett steg tilbake. */
            onEscapeKeyDown={(event) => {
              if (runEscapeStep()) event.preventDefault();
            }}
            onOpenAutoFocus={() => {
              restoreTo.current = document.activeElement;
            }}
            onCloseAutoFocus={(event) => {
              const target = restoreTo.current;
              if (target instanceof HTMLElement && target.isConnected) {
                event.preventDefault();
                target.focus();
              }
            }}
            // Radix wires aria-describedby to <Description> when there is one. When there is
            // not, it warns; passing the attribute as undefined is how you say 'deliberately none'.
            {...(description ? {} : { 'aria-describedby': undefined })}
            /*
             * HVOR DENNE STÅR, LESBART UTENFRA (ordre 4.5).
             *
             * DESIGN.md §A2 sier at det som redigerer noe er et SIDEPANEL og ikke en modal midt på
             * skjermen, og QA fant fire steder regelen ikke var fulgt. Uten et merke kan en test
             * bare måle geometri — «berører den høyre kanten?» — og det er en påstand om piksler
             * framfor om regelen. Attributtet gjør forskjellen til noe man kan spørre om.
             */
            data-placement={placement}
            className={cn(
              /* Glass 2: dialogen er et popover-lag (§16.3) — glass over innholdet. */
              'glass-popover pointer-events-auto flex w-full flex-col',
              'shadow-[var(--shadow-dialog)] outline-none',
              isSheet
                ? [
                    /*
                     * SKUFFEN LYTTET IKKE PÅ `size` (UX-ordren A2).
                     *
                     * `max-w-md` sto hardkodet her, så `size="xl"` ga 448 px og ikke 760. Panelet
                     * ble tegnet etter mockupens skjerm 7 og klemt inn i halvparten av bredden den
                     * er tegnet for — tidslinja fikk tolv timetall på 300 piksler.
                     *
                     * `md` er fortsatt standarden, og `max-w-lg` (512 px) er det `md` betyr — ikke
                     * `max-w-md`. De to gamle verdiene var ikke det samme tallet, så skuffene som
                     * ikke ber om noe blir 64 px bredere. Det er den ene forskjellen, og den er
                     * den samme bredden en sentrert `md`-dialog alt hadde.
                     */
                    'h-full rounded-none rounded-l-[var(--radius-card)]',
                    SIZE[size],
                    'data-[state=open]:animate-[tvitvi-sheet-in_var(--duration-base)_var(--ease-out)]',
                    'data-[state=closed]:animate-[tvitvi-sheet-out_var(--duration-base)_var(--ease-in)]',
                  ]
                : [
                    'max-h-[calc(100vh-2rem)] rounded-[var(--radius-card)]',
                    SIZE[size],
                    'data-[state=open]:animate-[tvitvi-dialog-in_var(--duration-base)_var(--ease-out)]',
                    'data-[state=closed]:animate-[tvitvi-dialog-out_var(--duration-fast)_var(--ease-in)]',
                  ],
              className,
            )}
          >
            {/*
              KRYSSET KAN ALDRI LIGGE UNDER INNHOLDET (QA 22. september, punkt 6).

              «X lukker ikke» i «Velg ressurs» og oppgavepanelet var ikke Radix og ikke
              `onOpenChange`: klikket nådde aldri krysset. Et bart `TimeField` (uten `Field` rundt
              seg) har en `absolute` klokkeknapp, og uten en posisjonert forelder forankret den seg
              på den faste innpakningen over — en 44 px høy stripe langs HELE høyre kant av
              skjermen, tegnet etter headeren og dermed oppå krysset. `elementFromPoint` midt på
              krysset svarte «Velg klokkeslett», og klikket åpnet klokka i stedet.

              To ting her gjør den feilklassen umulig for krysset, uansett hva kroppen inneholder:
              kroppen er `relative`, så noe som rømmer fra feltet sitt forankres i kroppen (og
              klippes av den) framfor i skjermen; og headeren er `relative z-10`, så den står over
              alt i kroppen som likevel skulle nå opp dit.
            */}
            <header
              className={cn(
                'relative z-10 flex items-start justify-between gap-3 px-4 py-3',
                titleHidden ? 'pb-0' : 'border-b border-default',
              )}
            >
              <div className="min-w-0">
                <Radix.Title
                  className={cn(
                    titleHidden
                      ? 'sr-only'
                      : 'truncate text-[length:var(--section-title-size)] font-semibold tracking-tight text-default',
                  )}
                >
                  {title}
                </Radix.Title>
                {description ? (
                  <Radix.Description className="mt-0.5 max-w-prose text-xs text-muted">
                    {description}
                  </Radix.Description>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                {headerActions}
                {dismissible ? (
                  <Radix.Close
                    aria-label={t('common.actions.close')}
                    className={cn(
                      'hit-target inline-flex size-7 shrink-0 items-center justify-center rounded-full',
                      'text-muted ring-focus',
                      'transition-colors duration-[var(--duration-fast)]',
                      'hover:bg-[var(--fill-tonal)] hover:text-default',
                    )}
                  >
                    <CloseIcon aria-hidden className="size-4" />
                  </Radix.Close>
                ) : null}
              </div>
            </header>

            {/*
              DEN ENE RULLEFLATEN (grep I1, R4). Kroppen ruller; ingenting inni skal ha sitt eget
              loddrette rullefelt. En tabell inni kleber hodet sitt mot DENNE flaten (`Table`), og
              `data-dialog-body` er det `one-scroll-surface.spec.ts` måler mot.
            */}
            <div
              data-dialog-body=""
              className="relative min-h-0 flex-1 overflow-auto p-4 text-xs text-default"
            >
              {/* Dialogen er selv arket: et kort eller en tabell inni tegnes uten egen ramme. */}
              <InsideSurface.Provider value>
                {viewer === undefined ? (
                  children
                ) : (
                  <RowViewerProvider viewer={viewer}>{children}</RowViewerProvider>
                )}
              </InsideSurface.Provider>
            </div>

            <ModalFooter
              footer={footer}
              primaryAction={primaryAction}
              secondaryActions={secondaryActions}
              cancel={cancel}
              readOnly={viewer !== undefined && viewer !== null && !viewer.canEdit}
              closeTestId={closeTestId}
              onClose={() => onOpenChange(false)}
            />
          </Radix.Content>
        </div>
      </Radix.Portal>
    </Radix.Root>
  );
}

interface ModalFooterProps {
  footer: ReactNode;
  primaryAction: ModalAction | undefined;
  secondaryActions: readonly ModalAction[] | undefined;
  cancel: ModalCancel | undefined;
  readOnly: boolean;
  closeTestId: string;
  onClose: () => void;
}

function actionButton(action: ModalAction, level: 'primary' | 'secondary'): ReactNode {
  const variant =
    level === 'primary' ? 'primary' : action.danger === true ? 'quietDanger' : 'secondary';
  return (
    <Button
      key={action.label}
      variant={variant}
      onClick={action.onClick}
      loading={action.loading ?? false}
      {...(action.disabledReason === undefined ? {} : { disabledReason: action.disabledReason })}
      {...(action.form === undefined ? {} : { type: 'submit' as const, form: action.form })}
      data-modal-action={level}
      data-testid={action['data-testid']}
    >
      {action.label}
    </Button>
  );
}

const FOOTER_CLASS = 'flex items-center justify-end gap-2 border-t border-default px-4 py-3';

/**
 * BUNNLINJA: fare til venstre, så sekundær, «Avbryt» og den blå ytterst til høyre (DESIGN.md §11:
 * én primærknapp, nederst i dialogen). I lesemodus er den én «Lukk».
 */
function ModalFooter({
  footer,
  primaryAction,
  secondaryActions = [],
  cancel,
  readOnly,
  closeTestId,
  onClose,
}: ModalFooterProps): ReactNode {
  const { t } = useTranslation();

  if (readOnly) {
    return (
      <footer className={FOOTER_CLASS} data-modal-footer="read-only">
        <Button onClick={onClose} data-modal-action="cancel" data-testid={closeTestId}>
          {t('common.actions.close')}
        </Button>
      </footer>
    );
  }

  const structured =
    primaryAction !== undefined ||
    secondaryActions.length > 0 ||
    (cancel !== undefined && cancel !== false);
  if (!structured) {
    return footer ? <footer className={FOOTER_CLASS}>{footer}</footer> : null;
  }

  const dangerous = secondaryActions.filter((action) => action.danger === true);
  const quiet = secondaryActions.filter((action) => action.danger !== true);
  const cancelSpec = typeof cancel === 'object' ? cancel : {};

  return (
    <footer className={FOOTER_CLASS} data-modal-footer="structured">
      {dangerous.length > 0 ? (
        <div className="me-auto flex items-center gap-2">
          {dangerous.map((action) => actionButton(action, 'secondary'))}
        </div>
      ) : null}
      {quiet.map((action) => actionButton(action, 'secondary'))}
      {cancel === undefined || cancel === false ? null : (
        <Button
          onClick={cancelSpec.onClick ?? onClose}
          data-modal-action="cancel"
          data-testid={cancelSpec['data-testid']}
        >
          {cancelSpec.label ?? t('common.actions.cancel')}
        </Button>
      )}
      {primaryAction === undefined ? null : actionButton(primaryAction, 'primary')}
    </footer>
  );
}

/** `asChild` trigger, so the caller's own Button stays the trigger and keeps focus restore. */
export const ModalTrigger = Radix.Trigger;
/** For a footer button that should close without the caller tracking `open` itself. */
export const ModalClose = Radix.Close;
